/**
 * Login Error Classification and Handling
 * Classifies login errors according to the canonical login flow requirements
 */

import type { AxiosError } from 'axios';

export enum LoginErrorType {
  /** User not found in database (404) */
  USER_NOT_PROVISIONED = 'USER_NOT_PROVISIONED',
  /** User inactive or role missing (403) */
  USER_NOT_AUTHORIZED = 'USER_NOT_AUTHORIZED',
  /** Backend system error (500, timeout, network) */
  SYSTEM_ERROR = 'SYSTEM_ERROR',
  /** MSAL authentication failure */
  AUTH_FAILURE = 'AUTH_FAILURE',
  /** Token expired - should trigger silent reauth */
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
}

export interface LoginError {
  type: LoginErrorType;
  message: string;
  userFacingMessage: string;
  action: 'block' | 'retry' | 'support';
  originalError?: any;
}

/**
 * Type guard to check if error is an Axios error
 */
function isAxiosError(error: any): error is AxiosError {
  return error?.isAxiosError === true || error?.response !== undefined;
}

/**
 * Classify login error based on HTTP status code and error details
 */
export function classifyLoginError(error: any): LoginError {
  // Handle Axios errors with proper type checking
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const errorMessage = error.message || '';

    // Network/timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('Network Error') || errorMessage.includes('ECONNABORTED')) {
      return {
        type: LoginErrorType.SYSTEM_ERROR,
        message: 'Backend timeout or network error',
        userFacingMessage: "We couldn't complete sign-in. You're signed in, but we're experiencing connection issues. Please try again in a moment.",
        action: 'retry',
        originalError: error,
      };
    }

  // HTTP status code classification
  switch (status) {
    case 404:
      return {
        type: LoginErrorType.USER_NOT_PROVISIONED,
        message: 'User not found in database (404)',
        userFacingMessage: "We couldn't complete sign-in. You're signed in, but your account isn't set up for this system. Please contact support to request access.",
        action: 'support',
        originalError: error,
      };

    case 403:
      return {
        type: LoginErrorType.USER_NOT_AUTHORIZED,
        message: 'User inactive or role missing (403)',
        userFacingMessage: "We couldn't complete sign-in. You're signed in, but your account has been disabled or you don't have the required permissions. Please contact your administrator.",
        action: 'block',
        originalError: error,
      };

    case 401:
      // Token expired or invalid
      if (errorMessage.includes('expired') || errorMessage.includes('token')) {
        return {
          type: LoginErrorType.TOKEN_EXPIRED,
          message: 'Token expired (401)',
          userFacingMessage: "Your session has expired. Please sign in again.",
          action: 'retry',
          originalError: error,
        };
      }
      return {
        type: LoginErrorType.AUTH_FAILURE,
        message: 'Authentication failed (401)',
        userFacingMessage: "We couldn't complete sign-in. Please try again.",
        action: 'retry',
        originalError: error,
      };

    case 500:
    case 502:
    case 503:
    case 504:
      return {
        type: LoginErrorType.SYSTEM_ERROR,
        message: `Backend error (${status})`,
        userFacingMessage: "We couldn't complete sign-in. You're signed in, but we're experiencing technical difficulties. Please try again in a moment or contact support if the problem persists.",
        action: 'retry',
        originalError: error,
      };

    default:
      // Unknown error - treat as system error
      return {
        type: LoginErrorType.SYSTEM_ERROR,
        message: errorMessage || `Unknown error (${status || 'no status'})`,
        userFacingMessage: "We couldn't complete sign-in. You're signed in, but we're unable to verify your account right now. Please try again or contact support if the problem persists.",
        action: 'retry',
        originalError: error,
      };
    }
  }

  // Handle non-Axios errors (e.g., generic Error objects)
  const errorMessage = error?.message || 'Unknown error';
  return {
    type: LoginErrorType.SYSTEM_ERROR,
    message: errorMessage,
    userFacingMessage: "We couldn't complete sign-in. You're signed in, but we're unable to verify your account right now. Please try again or contact support if the problem persists.",
    action: 'retry',
    originalError: error,
  };
}

/**
 * Extract user-friendly error message from error object
 */
export function getUserFacingErrorMessage(error: any): string {
  const classified = classifyLoginError(error);
  return classified.userFacingMessage;
}
